
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { listCandidates } from "../../../../core/data/redux/services/recruitmentApi";
import RecruitmentHeader from "../../components/RecruitmentHeader";
import CandidateFilters from "../components/CandidateFilters";
import CandidateTable from "../components/CandidateTable";
import Footer from "../../components/Footer";

interface Candidate {
  candidate_id: number;
  full_name: string;
  email: string;
  phone: string;
  current_designation: string | null;
  current_company: string | null;
  total_experience_years: number | null;
  key_skills: string[];
  total_applications: number;
  max_ats_score: string | null;
  avg_ats_score: string | null;
  last_applied_at: string | null;
  latest_status: string | null;
  profile_completion_percentage: number;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_candidates: number;
  per_page: number;
}

const CandidatesList: React.FC = () => {
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);
  const companyId = activeCompany?.company_id;
  const companyType = activeCompany?.company_type;

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    total_pages: 1,
    total_candidates: 0,
    per_page: 20,
  });

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchCandidates = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await listCandidates(String(companyId), String(companyType), {
        search: search || undefined,
        page,
        limit: 20,
      });
      setCandidates(data?.candidates ?? []);
      if (data?.pagination) setPagination(data.pagination);
    } catch (err) {
      console.error("[CandidatesList] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, companyType, search, page]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <RecruitmentHeader title="Candidates" breadcrumb="Candidates" />

        <CandidateFilters
          search={searchInput}
          onSearchChange={setSearchInput}
          onSearch={handleSearch}
          onReset={handleReset}
        />

        <div className="mt-4">
          <CandidateTable
            candidates={candidates}
            page={page}
            total={pagination.total_candidates}
            loading={loading}
            onPageChange={setPage}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CandidatesList;
